// Layout primitives for the shared legal documents. Inline styles only:
// this folder sits outside every app's Tailwind content glob, so utility
// classes written here would silently produce no CSS (see branon.md gotcha #4).
import type { CSSProperties, ReactNode } from 'react'

const ink = '#1f2937'
const body = '#374151'
const muted = '#6b7280'
const blue = '#2563eb'

export const card: CSSProperties = {
  background: '#ffffff',
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  padding: '2rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem',
  lineHeight: 1.65,
  color: body,
  fontSize: '0.95rem',
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 style={{ fontSize: '1.35rem', fontWeight: 600, color: ink, marginBottom: '0.9rem' }}>
        {title}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>{children}</div>
    </section>
  )
}

export function Sub({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 style={{ fontSize: '1.05rem', fontWeight: 600, color: ink, marginBottom: '0.4rem' }}>
        {title}
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>{children}</div>
    </div>
  )
}

export function P({ children }: { children: ReactNode }) {
  return <p style={{ margin: 0 }}>{children}</p>
}

export function Ul({ items }: { items: ReactNode[] }) {
  return (
    <ul style={{ margin: 0, paddingLeft: '1.4rem', listStyleType: 'disc', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  )
}

const tones: Record<string, { bg: string; border: string; text: string }> = {
  info: { bg: '#eff6ff', border: '#3b82f6', text: '#1e40af' },
  warn: { bg: '#fffbeb', border: '#f59e0b', text: '#92400e' },
  danger: { bg: '#fef2f2', border: '#ef4444', text: '#991b1b' },
  success: { bg: '#f0fdf4', border: '#22c55e', text: '#166534' },
}

export function Callout({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warn' | 'danger' | 'success'
  title?: string
  children: ReactNode
}) {
  const t = tones[tone]
  return (
    <div
      style={{
        background: t.bg,
        borderLeft: `4px solid ${t.border}`,
        borderRadius: 6,
        padding: '0.9rem 1.1rem',
        color: t.text,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
      }}
    >
      {title && <strong>{title}</strong>}
      {children}
    </div>
  )
}

export function A({ href, children, external }: { href: string; children: ReactNode; external?: boolean }) {
  return (
    <a
      href={href}
      style={{ color: blue, textDecoration: 'underline' }}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </a>
  )
}

export function ContactBlock({ email, operator }: { email: string; operator: string }) {
  return (
    <div
      style={{
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: 8,
        padding: '1.2rem 1.4rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.35rem',
      }}
    >
      <p style={{ margin: 0 }}>
        <strong>Email:</strong> <A href={`mailto:${email}`}>{email}</A>
      </p>
      <p style={{ margin: 0 }}>
        <strong>Operator:</strong> {operator}
      </p>
      <p style={{ margin: 0 }}>
        <strong>Response time:</strong> 48–72 hours
      </p>
    </div>
  )
}

export const pageNote: CSSProperties = {
  color: muted,
  fontSize: '0.85rem',
  textAlign: 'center',
  marginTop: '1rem',
}
